import { useEffect, useState } from "react";
import styled from "styled-components";
import { CheckCircle, Info, Sparkles, Target, Layers3, Clock3 } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { collection, query, where, orderBy, getDocs } from "firebase/firestore";
import { db } from "../../firebase";
const REACT_NATIVE = "./react-native.png";
const FLUTTER = "./flutter.png";
const ANDROID = "./android.png";
const SWIFT = "./swift.png";
const REACT = "./react.png";
const NEXT = "./nextjs.png";
const ANGULAR = "./angular.png";
const VUE = "./vue.png";
const NODE = "./node.png";
const DJANGO = "./django.png";
const SPRING = "./spring.png";
const GO = "./go.png";
const PYTHON = "./python.png";
/* ================= STYLES ================= */
const TECHNOLOGY_OPTIONS = {
  "Mobile apps (Android / iOS)": [
    {
      key: "react-native",
      title: "React Native",
      status: "live",
    },
    {
      key: "flutter",
      title: "Flutter",
      status: "coming",
    },
    {
      key: "android-kotlin",
      title: "Android (Kotlin)",
      status: "coming",
    },
    {
      key: "ios-swift",
      title: "iOS (Swift)",
      status: "coming",
    },
  ],

  "Websites & web apps": [
    {
      key: "react",
      title: "React",
      status: "coming",
    },
    {
      key: "nextjs",
      title: "Next.js",
      status: "coming",
    },
    {
      key: "angular",
      title: "Angular",
      status: "coming",
    },
    {
      key: "vue",
      title: "Vue",
      status: "coming",
    },
  ],

  "Backend logic (APIs & databases)": [
    {
      key: "node",
      title: "Node.js",
      status: "coming",
    },
    {
      key: "django",
      title: "Django",
      status: "coming",
    },
    {
      key: "spring",
      title: "Spring Boot",
      status: "coming",
    },
    {
      key: "go",
      title: "Go",
      status: "coming",
    },
  ],
};

const Page = styled.section`
  min-height: 100vh;
  padding: 120px 20px;
  display: flex;
  justify-content: center;
  background:
    radial-gradient(860px at 12% 0%, rgba(245, 158, 11, 0.12), transparent 58%),
    radial-gradient(900px at 88% 6%, rgba(56, 189, 248, 0.12), transparent 60%),
    linear-gradient(180deg, #06101d 0%, #040814 100%);
`;

const Card = styled.div`
  width: 100%;
  max-width: 1120px;
  display: grid;
  grid-template-columns: minmax(0, 0.92fr) minmax(280px, 0.42fr);
  gap: 22px;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const MainPanel = styled.div`
  padding: 30px;
  border-radius: 30px;
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.82), rgba(2, 6, 23, 0.72)),
    radial-gradient(circle at top left, rgba(56, 189, 248, 0.1), transparent 34%);
  border: 1px solid rgba(148, 163, 184, 0.14);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.42);
`;

const SidePanel = styled.div`
  padding: 24px;
  border-radius: 28px;
  background:
    linear-gradient(135deg, rgba(245, 158, 11, 0.12), rgba(56, 189, 248, 0.08)),
    rgba(15, 23, 42, 0.78);
  border: 1px solid rgba(245, 158, 11, 0.18);
  align-self: start;
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.1);
  border: 1px solid rgba(56, 189, 248, 0.18);
  color: #7dd3fc;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const IntroTitle = styled.h1`
  margin: 16px 0 0;
  font-size: clamp(28px, 4vw, 40px);
  line-height: 1.15;
  font-weight: 1000;
  color: #f8fafc;
`;

const IntroSub = styled.p`
  margin: 12px 0 0;
  color: #94a3b8;
  font-size: 15px;
  line-height: 1.7;
  max-width: 720px;
`;

const ProgressWrap = styled.div`
  margin: 24px 0 28px;
`;

const ProgressTop = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  color: #cbd5e1;
  margin-bottom: 10px;
`;

const ProgressBar = styled.div`
  height: 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${({ value }) => value}%;
  background: linear-gradient(90deg, #f59e0b, #38bdf8);
  transition: width 0.35s ease;
`;

const Question = styled.h2`
  font-size: 28px;
  font-weight: 900;
  margin-bottom: 28px;
  color: #f8fafc;
`;

const Options = styled.div`
  display: grid;
  gap: 14px;
`;

const Option = styled.button`
  padding: 18px 18px;
  border-radius: 18px;
  font-size: 16px;
  text-align: left;
  cursor: pointer;
  border: 1px solid
    ${({ active }) =>
      active ? "rgba(56,189,248,0.36)" : "rgba(148,163,184,0.12)"};
  background: ${({ active }) =>
    active
      ? "linear-gradient(135deg, rgba(56,189,248,0.14), rgba(245,158,11,0.1))"
      : "rgba(15, 23, 42, 0.46)"};
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 14px 34px rgba(0, 0, 0, 0.22);
  }
`;

const NavRow = styled.div`
  display: flex;
  gap: 14px;
  margin-top: 36px;
`;

const Btn = styled.button`
  flex: 1;
  padding: 15px;
  border-radius: 16px;
  font-size: 16px;
  font-weight: 800;
  cursor: pointer;
  border: none;
  background: ${({ variant }) =>
    variant === "secondary"
      ? "rgba(255,255,255,0.08)"
      : "linear-gradient(90deg,#f59e0b,#d97706)"};
  color: white;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`;

/* ================= CLARITY ================= */

const InfoBox = styled.div`
  padding: 20px;
  border-radius: 20px;
  background: rgba(15, 23, 42, 0.54);
  border: 1px solid rgba(148, 163, 184, 0.14);
  display: flex;
  gap: 14px;
  line-height: 1.6;
  font-size: 15px;
`;
const TechGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 14px;
  margin-top: 18px;
`;
const TechCard = styled.div`
  position: relative;
  padding: 18px 14px;
  border-radius: 16px;
  text-align: center;

  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};

  background: ${({ active }) =>
    active
      ? "linear-gradient(135deg, rgba(56,189,248,0.18), rgba(245,158,11,0.12))"
      : "rgba(255,255,255,0.04)"};

  border: 1px solid
    ${({ active }) =>
      active ? "rgba(56,189,248,0.44)" : "rgba(255,255,255,0.1)"};

  opacity: ${({ disabled }) => (disabled ? 0.45 : 1)};
  filter: ${({ disabled }) => (disabled ? "grayscale(100%)" : "none")};

  transition: all 0.25s ease;

  &:hover {
    transform: ${({ disabled }) => (disabled ? "none" : "translateY(-4px)")};
    box-shadow: ${({ disabled }) =>
      disabled ? "none" : "0 10px 28px rgba(0,0,0,0.25)"};
  }
`;
const TechTitle = styled.h4`
  font-size: 15px;
  font-weight: 800;
  margin-bottom: 6px;
`;
const TechSub = styled.div`
  font-size: 13px;
  font-weight: 600;
  margin-top: 6px;
  min-height: 18px;
  color: ${({ theme }) => theme.colors.textSecondary};

  ${({ hidden }) =>
    hidden &&
    `
      visibility: hidden;
    `}
`;
const Badge = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;

  padding: 4px 10px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.4px;
  text-transform: uppercase;

  color: #111;
  background: linear-gradient(135deg, #facc15, #f59e0b);
  border-radius: 999px;

  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
`;

const SideLabel = styled.div`
  color: #fbbf24;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const SideTitle = styled.div`
  margin-top: 10px;
  color: #f8fafc;
  font-size: 24px;
  line-height: 1.2;
  font-weight: 1000;
`;

const SideText = styled.p`
  margin-top: 10px;
  color: #cbd5e1;
  font-size: 14px;
  line-height: 1.7;
`;

const SideList = styled.div`
  margin-top: 18px;
  display: grid;
  gap: 10px;
`;

const SideItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 16px;
  background: rgba(2, 6, 23, 0.48);
  border: 1px solid rgba(148, 163, 184, 0.12);
  color: #cbd5e1;
  font-size: 13px;
  font-weight: 700;
`;
const TechIcon = styled.img`
  width: 100px;
  height: 100px;
  // margin-bottom: 10px;
  object-fit: contain;
`;
const TECH_IMAGES = {
  "react-native": REACT_NATIVE,
  flutter: FLUTTER,
  "android-kotlin": ANDROID,
  "ios-swift": SWIFT,

  react: REACT,
  nextjs: NEXT,
  angular: ANGULAR,
  vue: VUE,

  node: NODE,
  django: DJANGO,
  spring: SPRING,
  go: GO,
};

/* ================= COMPONENT ================= */

export default function CareerClarityTest() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [selectedTech, setSelectedTech] = useState(null);
  const selectedPath = state?.path || "product";

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [step, setStep] = useState(0);
  const [showClarity, setShowClarity] = useState(false);
  const [answers, setAnswers] = useState({});

  /* ================= FETCH QUESTIONS ================= */

  useEffect(() => {
    const fetchQuestions = async () => {
      const roadmapType = selectedPath === "dsa" ? "DSA" : "PRODUCT";

      const q = query(
        collection(db, "roadmapQuestions"),
        where("roadmapType", "==", roadmapType),
        where("isActive", "==", true)
        //  orderBy("order", "asc")
      );

      const snap = await getDocs(q);

      const data = snap.docs
        .map((d) => ({
          id: d.id, // stable id
          order: d.data().order, // business order
          question: d.data().question,
          options: d.data().options,
          type: d.data().type,
        }))
        .sort((a, b) => a.order - b.order); // 👈 SORT HERE
      setQuestions(data);

      setLoading(false);
    };

    fetchQuestions();
  }, [selectedPath]);

  if (loading) {
    return (
      <Page>
        <Card>
          <MainPanel>
            <Eyebrow>
              <Sparkles size={14} />
              Career Clarity Test
            </Eyebrow>
            <IntroTitle>Loading your questions…</IntroTitle>
            <IntroSub>
              We are preparing the questions that match your chosen direction.
            </IntroSub>
          </MainPanel>
        </Card>
      </Page>
    );
  }

  const QUESTIONS = questions;
  const current = QUESTIONS[step];

  /* ================= CLARITY LOGIC ================= */

  const isClarityStep = selectedPath !== "dsa" && current?.order === 3;

  const interestQuestion = QUESTIONS.find((q) => q.order === 2);
  const interest = answers.normalized?.interest || null;

  /* ================= PROGRESS ================= */

  const totalSteps =
    selectedPath === "dsa" ? QUESTIONS.length : QUESTIONS.length + 1;

  const progressPercent = Math.round(
    ((step + (showClarity ? 1 : 0) + 1) / totalSteps) * 100
  );

  /* ================= ACTIONS ================= */
  const selectOption = (option) => {
    setAnswers((prev) => ({
      ...prev,

      raw: {
        ...prev.raw,
        [current.id]: option, // backup / analytics
      },

      normalized: {
        ...prev.normalized,
        [current.type]: option, // 🔥 THIS IS IMPORTANT
      },
    }));
  };
  function getAnswerByOrder(answers, questions, order) {
    const q = questions.find((q) => q.order === order);
    return q ? answers[q.id] : null;
  }
  const next = () => {
    if (isClarityStep && !showClarity) {
      setShowClarity(true);
      return;
    }

    if (step < QUESTIONS.length - 1) {
      setStep(step + 1);
      setShowClarity(false);
    } else {
      navigate("/career-clarity/result", {
        state: {
          path: selectedPath,
          technology: selectedTech,
          normalizedAnswers: {
            profile: answers, // raw answers (backup)
            interest, // Mobile apps / Frontend / Backend
          },
        },
      });
    }
  };

  const prev = () => {
    if (showClarity) {
      setShowClarity(false);
      return;
    }
    if (step > 0) setStep(step - 1);
  };

  /* ================= RENDER ================= */

  return (
    <Page>
      <Card>
        <MainPanel>
          <Eyebrow>
            <Sparkles size={14} />
            Career Clarity Test
          </Eyebrow>
          <IntroTitle>
            {showClarity
              ? "Choose the technology you want to start with"
              : "Answer a few focused questions to get your direction"}
          </IntroTitle>
          <IntroSub>
            {showClarity
              ? "This helps us turn your chosen direction into a more personalized roadmap start."
              : "You are not being judged here. We are just reducing confusion so the next path actually fits your goals."}
          </IntroSub>

          <ProgressWrap>
            <ProgressTop>
              <span>
                Step {Math.min(step + (showClarity ? 2 : 1), totalSteps)} of {totalSteps}
              </span>
              <span>{progressPercent}% clear</span>
            </ProgressTop>
            <ProgressBar>
              <ProgressFill value={progressPercent} />
            </ProgressBar>
          </ProgressWrap>

          {showClarity ? (
            <>
              <Question>Choose what you want to start with</Question>

              <InfoBox>
                <Info size={20} />
                <div style={{ width: "100%" }}>
                  <b>Start with one option</b>
                  <br />
                  This helps us personalize your roadmap.
                  <br />
                  <TechGrid>
                    {TECHNOLOGY_OPTIONS[interest]?.map((tech) => {
                      const disabled = tech.status !== "live";
                      const active = answers.normalized?.technology === tech.key;

                      return (
                        <TechCard
                          key={tech.key}
                          active={active}
                          disabled={disabled}
                          onClick={() => {
                            if (disabled) return;

                            setSelectedTech(tech.key);

                            setAnswers((prev) => ({
                              ...prev,
                              normalized: {
                                ...prev.normalized,
                                technology: tech.key,
                              },
                            }));
                          }}
                        >
                          <TechIcon
                            src={TECH_IMAGES[tech.key]}
                            alt={tech.title}
                          />

                          <TechTitle>{tech.title}</TechTitle>

                          {tech.status === "live" ? (
                            <TechSub>Available now</TechSub>
                          ) : (
                            <Badge>Coming soon</Badge>
                          )}
                        </TechCard>
                      );
                    })}
                  </TechGrid>
                </div>
              </InfoBox>

              <NavRow>
                <Btn variant="secondary" onClick={prev}>
                  ← Back
                </Btn>
                <Btn onClick={next} disabled={!selectedTech}>
                  Continue →
                </Btn>
              </NavRow>
            </>
          ) : (
            <>
              <Question>{current.question}</Question>

              <Options>
                {current.options.map((opt) => {
                  const isActive = answers.normalized?.[current.type] === opt;

                  return (
                    <Option
                      key={opt}
                      active={isActive}
                      onClick={() => selectOption(opt, current.type)}
                    >
                      {opt}
                      {isActive && <CheckCircle size={18} />}
                    </Option>
                  );
                })}
              </Options>

              <NavRow>
                <Btn variant="secondary" onClick={prev} disabled={step === 0}>
                  ← Previous
                </Btn>
                <Btn
                  onClick={next}
                  disabled={!answers.normalized?.[current.type]}
                >
                  {step === QUESTIONS.length - 1 ? "See My Result →" : "Next →"}
                </Btn>
              </NavRow>
            </>
          )}
        </MainPanel>

        <SidePanel>
          <SideLabel>Why this matters</SideLabel>
          <SideTitle>Clarity saves months of wrong effort</SideTitle>
          <SideText>
            The goal here is simple: reduce confusion, identify the right path,
            and make sure your roadmap starts from a direction that actually
            fits you.
          </SideText>

          <SideList>
            <SideItem>
              <Target size={16} />
              Questions are focused on direction, not trivia
            </SideItem>
            <SideItem>
              <Layers3 size={16} />
              Your answers shape the recommended path and roadmap
            </SideItem>
            <SideItem>
              <Clock3 size={16} />
              Takes only a few minutes to complete
            </SideItem>
          </SideList>
        </SidePanel>
      </Card>
    </Page>
  );
}
